import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import PersonDetailPage from './PersonDetailPage';
import type { PersonData } from '../components/person/types';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import PublicLayout from '@shared/ui/PublicLayout';

const PublicPersonPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [person, setPerson] = useState<PersonData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const typeHint = location.pathname.includes('/legislators/') ? 'legislator'
    : location.pathname.includes('/candidates/') ? 'candidate'
    : undefined;

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);

    const params = typeHint ? `?type=${typeHint}` : '';
    axios.get(`/api/people/public/${id}${params}`)
      .then(res => setPerson(res.data.data))
      .catch(() => setError('Person not found'))
      .finally(() => setLoading(false));
  }, [id, typeHint]);

  // The shared public chrome (header, responsive gutters, footer) — the same
  // frame the public bill and ballot pages use. Rendering the detail page bare
  // left the cards flush against the screen edge on a phone (Angie, 2026-09-13).
  if (loading) {
    return (
      <PublicLayout showFooter={false}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner size="lg" />
        </div>
      </PublicLayout>
    );
  }

  if (error || !person) {
    return (
      <PublicLayout backHref="/" backLabel="Back to Capitol Commons">
        <div className="p-6 text-center">
          <p className="text-text-secondary dark:text-text-dark-secondary">
            {error || 'Person not found'}
          </p>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <PersonDetailPage isPublic person={person} />
    </PublicLayout>
  );
};

export default PublicPersonPage;
